import {
  createDocument,
  updateDocument,
  deleteDocument,
  getDocument,
  getCollection,
} from "./FirebaseService";
import { getDocs, collection, query, orderBy, startAt, endAt, where,
  limit as limitFn } from "firebase/firestore";
import { db } from "../auth/firebase";

const COLLECTION = "agents";

const mapToAgent = (data) => ({
  agentName: data.agentName || "",
  agentCode: data.agentCode || "",
  email: data.email || "",
  phone: data.phone || "",
  address: data.address || "",
  commissionRate: Number(data.commissionRate) || 0,
  status: data.status || "active",
  notes: data.notes || "",
  agreementRenewed: data.agreementRenewed || "No",
  agreementRenewedDate: data.agreementRenewedDate || "",
  createdAt: data.createdAt || new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});
const mapToNewAgentApplication = (data) => ({
  ...data,
  agentName: data.companyDetails.companyName || "",
  email: data.companyDetails.email || "",
  phone: data.companyDetails.phone || "",
  address: data.companyDetails.address || "",
  createdAt: data.createdAt || new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});
const mapToAgentUpdation = (data) => ({
  ...data,
  agentAcceptedAt: new Date().toISOString(),
  createdAt: data.createdAt || new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});


// Create a new agent document (optional ID for update or custom doc ID)
export const createAgent = async (data, id) =>
  await createDocument(COLLECTION, mapToAgent(data), id);

export const createNewAgentApplication = async (data, id) =>
  await createDocument(COLLECTION, mapToNewAgentApplication(data), id);

export const updateAgentApplication = async (id, data) => {
  const agent = mapToAgentUpdation(data);
  return await updateDocument(COLLECTION, id, agent);
};
// Update an existing agent document
export const updateAgent = async (id, data) =>
  await updateDocument(COLLECTION, id, mapToAgent(data));

// Delete an agent document
export const deleteAgent = async (id) => await deleteDocument(COLLECTION, id);

// Get a single agent document by ID
export const getAgent = async (id) => await getDocument(COLLECTION, id);

// Get all agent documents
export const getAllAgents = async () => await getCollection(COLLECTION);
export const getNewAgents = async () => {
  try {
    const q = query(collection(db, COLLECTION), where("status", "==", "new"));
    const snapshot = await getDocs(q);
    return snapshot.size; // number of pending leaves
  } catch (error) {
    console.error("Failed to fetch new agent count:", error);
    return 0;
  }
};
export const searchAgent = async (searchText = "") => {
  const agentsRef = collection(db, COLLECTION);
  // No search -> return everything (you may want to page this in real app)
  if (!searchText || !searchText.trim() || searchText === "") {
    const snapshot = await getDocs(agentsRef);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  }

  const s = searchText.trim().toLowerCase();
  const end = s + "\uf8ff";

  // Quick check: do docs already contain lowercased search fields?
  try {
    const sampleSnap = await getDocs(query(agentsRef, orderBy("__name__"), limitFn(1)));
    const sampleDoc = sampleSnap.docs[0];
    const sampleData = sampleDoc ? sampleDoc.data() : null;

    const hasLowerFields =
      sampleData &&
      (Object.prototype.hasOwnProperty.call(sampleData, "agentCode_lower") ||
        Object.prototype.hasOwnProperty.call(sampleData, "agentName_lower") );

    if (hasLowerFields) {
      // run prefix queries in parallel on the lowercased fields
      const fieldsToQuery = ["agentCode_lower", "agentName_lower"];
      const queries = fieldsToQuery.map((field) =>
        query(agentsRef, orderBy(field), startAt(s), endAt(end))
      );

      const snaps = await Promise.all(queries.map((q) => getDocs(q).catch(() => ({ docs: [] }))));

      const results = [];
      snaps.forEach((snap) =>
        snap.docs.forEach((doc) => {
          results.push({ id: doc.id, ...doc.data() });
        })
      );

      // dedupe
      const unique = Object.values(results.reduce((acc, cur) => {
        acc[cur.id] = acc[cur.id] || cur;
        return acc;
      }, {}));

      return unique;
    } else {
      // FALLBACK: no lowercased fields -> client-side filter (inefficient for large collections)
      const allSnap = await getDocs(agentsRef);
      const all = allSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const filtered = all.filter((st) => {
        const fs = st.formState || st; // in your code agent data is nested in formState
        const agentCode = (fs.agentCode || "").toString().toLowerCase();
        const agentName = (fs.agentName || "").toLowerCase();

        return (
          agentCode.includes(s) ||
          agentName.includes(s) 
        );
      });

      return filtered;
    }
  } catch (err) {
    return [];
  }
};
export const getAgentByName = async (name) => {
  if (!name || !name.trim()) return null;

  try {
    const agentsRef = collection(db, COLLECTION);
    const q = query(agentsRef, where("agentName", "==", name.trim()), limitFn(1));
    const snapshot = await getDocs(q);

    if (snapshot.empty) return null;

    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (err) {
    console.error("Error fetching agent by name:", err);
    return null;
  }
};

export const addPaidCommissionToAgent = async (agentId, commissionData, studentId) => {
  if (!agentId) throw new Error("Agent ID is required");
  if (!commissionData) throw new Error("Commission data is required");

  try {
    // Fetch current agent data
    const agent = await getDocument(COLLECTION, agentId);
    if (!agent) throw new Error("Agent not found");
    const studentCommissions = agent?.studentCommissions ? agent.studentCommissions : {};
    // update doctionary if studentID is present in the dictionary 
    // Initialize array for this student if missing
    studentCommissions[studentId] = {
      ...commissionData,
      savedAt: new Date().toISOString(),
    };
    // otherwise add in dictioanry and then 
    // save it in the agent records keeps rest same.
    // Update agent document with new array
    await updateDocument("agents", agentId, {
        ...agent, // keep all existing data
        studentCommissions: studentCommissions,
        updatedAt: new Date().toISOString(),
    });
  } catch (err) {
    console.error("Failed to add paid commission to agent:", err);
    throw err;
  }
};